import React, { useCallback, useEffect, useState } from 'react';
import Tooltip from '../components/ui/Tooltip';
import { errorMessage, getTopPoints } from './api';
import { ThumbIcon } from './PointRow';
import type { PlanTree, TopPoint } from './types';
import Alert from '../components/ui/Alert';
import FadeIn from '../components/ui/FadeIn';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';
const MOTION = 'transition-colors duration-150 ease-emphasized motion-reduce:transition-none';

export interface MostLikedPanelProps {
  planId: string;
  tree: PlanTree;
  /** Bumped by the page when the plan reloads, so the list follows new votes. */
  version: number;
  onOpen: (nodeId: string) => void;
}

type Load = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; points: TopPoint[] };

const name = (a: TopPoint['author']) => `${a.firstname || ''} ${a.lastname || ''}`.trim() || 'Former member';

/** The plan's most voted points across every topic, most votes first. */
const MostLikedPanel: React.FC<MostLikedPanelProps> = ({ planId, tree, version, onOpen }) => {
  const [load, setLoad] = useState<Load>({ state: 'loading' });

  const fetchTop = useCallback(async () => {
    setLoad({ state: 'loading' });
    try {
      const { points } = await getTopPoints(planId, 20);
      setLoad({ state: 'ready', points });
    } catch (e) {
      setLoad({ state: 'error', message: errorMessage(e, 'Could not load the most liked notes.') });
    }
  }, [planId]);

  useEffect(() => {
    let live = true;
    getTopPoints(planId, 20)
      .then(({ points }) => live && setLoad({ state: 'ready', points }))
      .catch((e) => live && setLoad({ state: 'error', message: errorMessage(e, 'Could not load the most liked notes.') }));
    return () => {
      live = false;
    };
  }, [planId, version]);

  if (load.state === 'loading') {
    return (
      <FadeIn key="loading">
        <div aria-busy="true">
          <SkeletonGroup label="Loading the most liked notes" className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </SkeletonGroup>
        </div>
      </FadeIn>
    );
  }

  if (load.state === 'error') {
    return (
      <FadeIn key="error">
        <Alert onRetry={fetchTop}>{load.message}</Alert>
      </FadeIn>
    );
  }

  if (load.points.length === 0) {
    return (
      <FadeIn key="empty">
        <EmptyState variant="area" title="No votes yet.">
          Vote for a note under any topic and it shows up here.
        </EmptyState>
      </FadeIn>
    );
  }

  const numberOf = new Map(tree.sections.map((s) => [s.id, s.number]));
  const top = load.points[0].votes;

  return (
    <FadeIn key="ready">
    <ol className="mb-0 -mx-1 space-y-0.5">
      {load.points.map((p) => {
        const number = numberOf.get(p.node_id);
        return (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onOpen(p.node_id)}
              className={`np-state grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-3 rounded-lg px-2.5 py-2 text-left ${MOTION} ${FOCUS}`}
            >
              <span className="min-w-0">
                <Tooltip label={p.node_title} truncated>
                  <span className="block truncate text-xs text-on-surface-variant">
                    {number && <span className="mr-1 font-mono tabular-nums">{number}</span>}
                    {p.node_title}
                  </span>
                </Tooltip>
                <span className="mt-0.5 whitespace-pre-wrap break-words text-sm text-on-surface line-clamp-3">{p.body}</span>
                <span className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-on-surface-variant">
                  {name(p.author)}
                  {p.adopted && (
                    <span className="inline-flex items-center rounded-full bg-primary/15 px-2 py-px text-[11px] font-semibold text-primary-ink">
                      Adopted
                    </span>
                  )}
                </span>
              </span>
              <span
                className={`inline-flex items-center gap-1 self-start pt-0.5 text-sm font-semibold tabular-nums ${
                  p.votes === top ? 'text-on-surface' : 'text-on-surface-variant'
                }`}
              >
                <ThumbIcon />
                <span>
                  {p.votes}
                  <span className="sr-only">{p.votes === 1 ? ' vote' : ' votes'}</span>
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
    </FadeIn>
  );
};

export default MostLikedPanel;
